import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaCheckCircle, FaTimes, FaEye, FaDownload,
  FaBuilding, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt,
  FaUniversity, FaFileAlt, FaDollarSign, FaCalendarAlt, FaBriefcase,
  FaSpinner, FaExclamationTriangle, FaCheck, FaBan, FaClock,
  FaFilePdf, FaFileImage, FaExternalLinkAlt, FaIdCard,
  FaRegFilePdf, FaImage, FaQuestionCircle, FaInfoCircle,
  FaShieldAlt, FaUserTie, FaUsers
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function AdminClientApprovalDetail({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [user, setUser] = useState({});
  const [processing, setProcessing] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [references, setReferences] = useState([]);
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // ✅ Verification state - references_verified removed from here
  const [verification, setVerification] = useState({
    proof_of_account_verified: false,
    cipc_document_verified: false,
    director_id_verified: false,
    proof_of_address_verified: false,
    bank_statements_verified: false
  });

  // ✅ Separate state for reference verification
  const [referencesVerified, setReferencesVerified] = useState(false);

  const [verificationNotes, setVerificationNotes] = useState({
    proof_of_account_verified: '',
    cipc_document_verified: '',
    director_id_verified: '',
    proof_of_address_verified: '',
    bank_statements_verified: ''
  });

  // Credit approval fields
  const [approveData, setApproveData] = useState({
    approved_credit_limit: '',
    approved_payment_terms: '30 Days'
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchClientDetail();
  }, [id]);

  const fetchClientDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/clients/${id}`);
      const data = await response.json();
      if (data.success) {
        setClient(data.client);
        setReferences(data.client.references || []);
        // If already approved or rejected, load verification states
        if (data.client.status === 'active' || data.client.status === 'rejected') {
          loadVerificationStates(data.client);
        }
      } else {
        toast.error(data.error || 'Failed to load client details');
        navigate('/admin/client-approval');
      }
    } catch (err) {
      console.error('Error fetching client detail:', err);
      toast.error('Failed to load client details');
      navigate('/admin/client-approval');
    } finally {
      setLoading(false);
    }
  };

  const loadVerificationStates = (data) => {
    if (data.status === 'active') {
      const allVerified = {};
      Object.keys(verification).forEach(key => {
        allVerified[key] = true;
      });
      setVerification(allVerified);
      // Also set references as verified
      setReferencesVerified(true);
    }
  };

  const toggleVerify = (field) => {
    setVerification(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const toggleReferencesVerified = () => {
    setReferencesVerified(!referencesVerified);
  };

  const handleVerifyNote = (field, value) => {
    setVerificationNotes(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getDocumentUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return window.getServerUrl(cleanUrl);
  };

  const viewDocument = (docUrl, docName) => {
    if (!docUrl) {
      toast.warning('No document uploaded');
      return;
    }
    const fullUrl = getDocumentUrl(docUrl);
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    } else {
      toast.error('Could not open document');
    }
  };

  const downloadDocument = (docUrl, docName) => {
    if (!docUrl) {
      toast.warning('No document to download');
      return;
    }
    const fullUrl = getDocumentUrl(docUrl);
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    } else {
      toast.error('Could not download document');
    }
  };

  const getDocumentIcon = (url) => {
    if (!url) return <FaFileAlt />;
    if (url.toLowerCase().includes('.pdf')) return <FaFilePdf className="text-red-500" />;
    if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg') || url.toLowerCase().includes('.png')) {
      return <FaFileImage className="text-blue-500" />;
    }
    return <FaFileAlt />;
  };

  const getDocumentLabel = (url) => {
    if (!url) return 'No document';
    if (url.toLowerCase().includes('.pdf')) return 'PDF';
    if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg')) return 'JPEG';
    if (url.toLowerCase().includes('.png')) return 'PNG';
    return 'Document';
  };

  const handleApproveClick = () => {
    // Check all documents are verified
    const allDocsVerified = Object.values(verification).every(v => v === true);
    
    if (!allDocsVerified) {
      toast.warning('Please verify all documents before approving');
      return;
    }

    // For credit clients, check references and credit fields
    if (client.client_type === 'credit') {
      if (!referencesVerified) {
        toast.warning('Please verify trade references before approving');
        return;
      }
      if (!approveData.approved_credit_limit || parseFloat(approveData.approved_credit_limit) <= 0) {
        toast.error('Please enter an approved credit limit');
        return;
      }
      if (!approveData.approved_payment_terms) {
        toast.error('Please select approved payment terms');
        return;
      }
    }

    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    setProcessing(true);
    setShowApproveModal(false);
    
    try {
      const payload = {
        status: 'active',
        admin_notes: adminNotes || 'All documents verified and approved.',
        admin_id: user.id,
        verification_details: {
          ...verification,
          references_verified: referencesVerified
        },
        verification_notes: verificationNotes
      };

      if (client.client_type === 'credit') {
        payload.approved_credit_limit = parseFloat(approveData.approved_credit_limit);
        payload.approved_payment_terms = approveData.approved_payment_terms;
      }

      const response = await fetch(`${API_URL}/clients/${id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Client approved successfully!');
        navigate('/admin/client-approval');
      } else {
        toast.error(data.error || 'Failed to approve client');
      }
    } catch (err) {
      console.error('Error approving client:', err);
      toast.error('Failed to approve client');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    setShowRejectModal(false);
    
    try {
      const response = await fetch(`${API_URL}/clients/${id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'rejected',
          admin_notes: adminNotes || `Rejected. Reason: ${rejectReason}`,
          rejection_reason: rejectReason,
          admin_id: user.id
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('❌ Client rejected');
        navigate('/admin/client-approval');
      } else {
        toast.error(data.error || 'Failed to reject client');
      }
    } catch (err) {
      console.error('Error rejecting client:', err);
      toast.error('Failed to reject client');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'active': 'bg-green-100 text-green-600',
      'inactive': 'bg-gray-100 text-gray-600',
      'rejected': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Review',
      'active': 'Active',
      'inactive': 'Inactive',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  const getClientTypeBadge = (type) => {
    const styles = {
      'prepaid': 'bg-blue-100 text-blue-600',
      'credit': 'bg-purple-100 text-purple-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[type] || 'bg-gray-100 text-gray-600'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'R 0.00';
    return `R ${parseFloat(amount).toFixed(2)}`;
  };

  const isCredit = client?.client_type === 'credit';
  
  // Get verification fields list - references_verified removed
  const verificationFields = Object.keys(verification);
  const totalItems = verificationFields.length + (isCredit ? 1 : 0); // +1 for references
  const verifiedCount = verificationFields.filter(field => verification[field] === true).length + (referencesVerified ? 1 : 0);
  const allDocsVerified = verificationFields.every(field => verification[field] === true);
  const allVerified = allDocsVerified && (isCredit ? referencesVerified : true);

  // ✅ Verification Item component
  const VerificationItem = ({ field, label, icon, docUrl, docField }) => {
    const isVerified = verification[field] || false;
    const note = verificationNotes[field] || '';
    const hasDocument = !!docUrl;
    const docIcon = getDocumentIcon(docUrl);
    const docLabel = getDocumentLabel(docUrl);

    if (!verification.hasOwnProperty(field)) return null;

    return (
      <div className="flex items-start gap-3 p-3 bg-white rounded-lg border" style={{ borderColor: isVerified ? '#00A86B' : '#E8ECEF' }}>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {icon}
            <span className="font-medium text-sm" style={{ color: '#0A1929' }}>{label}</span>
            {isVerified ? (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <FaCheckCircle size={12} /> Verified
              </span>
            ) : (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <FaClock size={12} /> Pending
              </span>
            )}
          </div>
          
          {/* Document buttons */}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {hasDocument ? (
              <>
                <button
                  onClick={() => downloadDocument(docUrl, label)}
                  className="text-xs bg-gray-50 text-gray-600 hover:bg-gray-100 px-3 py-1 rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <FaDownload size={10} />
                  <span>Download</span>
                </button>
                <span className="text-xs text-gray-400">✓ Uploaded</span>
              </>
            ) : (
              <span className="text-xs text-gray-400">⚠️ No document uploaded</span>
            )}
          </div>

          <input
            type="text"
            placeholder="Add verification note..."
            value={note}
            onChange={(e) => handleVerifyNote(field, e.target.value)}
            className="w-full mt-1.5 px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-green"
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout activeTab="clients" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading client details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!client) {
    return (
      <AdminLayout activeTab="clients" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">📋</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Client Not Found</h2>
          <p className="text-gray-500">The client application you're looking for doesn't exist.</p>
          <Link to="/admin/client-approval" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            Back to Applications
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="clients" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/client-approval" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
            <FaArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
              <FaBuilding className="inline mr-2" style={{ color: '#00A86B' }} />
              Client Verification
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <span className="font-semibold" style={{ color: '#0A1929' }}>{client.company_name}</span>
              <span className={getStatusBadge(client.status)}>
                {getStatusLabel(client.status)}
              </span>
              <span className={getClientTypeBadge(client.client_type)}>
                {client.client_type || 'prepaid'}
              </span>
              {isCredit && (
                <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                  Requested: {formatCurrency(client.requested_credit_limit)}
                </span>
              )}
              <span className="text-sm text-gray-500">
                Submitted: {formatDate(client.created_at)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {verifiedCount}/{totalItems} Verified
          </span>
          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${totalItems > 0 ? (verifiedCount / totalItems) * 100 : 0}%`,
                backgroundColor: allVerified ? '#00A86B' : '#F59E0B'
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Client Details & Verification */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* COMPANY INFORMATION - UPDATED with city, province, postal_code */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaBuilding className="inline mr-2" style={{ color: '#00A86B' }} />
              Company Information
            </h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Company Name</span>
                <span className="font-medium text-navy">{client.company_name}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Trading Name</span>
                <span className="font-medium text-navy">{client.trading_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Registration Number</span>
                <span className="font-medium text-navy">{client.registration_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">VAT Number</span>
                <span className="font-medium text-navy">{client.vat_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Company Type</span>
                <span className="font-medium text-navy">{client.company_type || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Payment Terms</span>
                <span className="font-medium text-navy">{client.payment_terms || '30 Days'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">City</span>
                <span className="font-medium text-navy">{client.city || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Province</span>
                <span className="font-medium text-navy">{client.province || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Postal Code</span>
                <span className="font-medium text-navy">{client.postal_code || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* CONTACT INFORMATION */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaUser className="inline mr-2" style={{ color: '#00A86B' }} />
              Contact Information
            </h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Contact Person</span>
                <span className="font-medium text-navy">{client.contact_person || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Email</span>
                <span className="font-medium text-navy">{client.contact_email || client.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Phone</span>
                <span className="font-medium text-navy">{client.contact_phone || client.phone || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Mobile</span>
                <span className="font-medium text-navy">{client.mobile || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Address</span>
                <span className="font-medium text-navy text-right">{client.physical_address || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* BANKING DETAILS */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaUniversity className="inline mr-2" style={{ color: '#00A86B' }} />
              Banking Details
            </h2>
            {client.bank_name || client.account_holder ? (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Bank Name</span>
                  <span className="font-medium text-navy">{client.bank_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Account Holder</span>
                  <span className="font-medium text-navy">{client.account_holder || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Account Number</span>
                  <span className="font-medium text-navy">{client.account_number || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Branch Code</span>
                  <span className="font-medium text-navy">{client.branch_code || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Account Type</span>
                  <span className="font-medium text-navy">{client.account_type || 'N/A'}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-2">No banking details provided</p>
            )}
          </div>

          {/* CREDIT INFORMATION (Only for Credit Clients) */}
          {isCredit && (
            <div className="bg-white rounded-xl border p-6 border-purple-200 bg-purple-50/30">
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
                <FaDollarSign className="inline mr-2" style={{ color: '#00A86B' }} />
                Credit Information
              </h2>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between py-1 border-b border-purple-100">
                  <span className="text-gray-600">Requested Credit Limit</span>
                  <span className="font-medium text-purple-700">{formatCurrency(client.requested_credit_limit)}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-purple-100">
                  <span className="text-gray-600">Requested Payment Terms</span>
                  <span className="font-medium text-purple-700">{client.requested_payment_terms || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-purple-100">
                  <span className="text-gray-600">Billing Contact</span>
                  <span className="font-medium text-navy">{client.billing_contact_person || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Billing Email</span>
                  <span className="font-medium text-navy">{client.billing_email || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* TRADE REFERENCES with Verify Button */}
          {isCredit && references && references.length > 0 && (
            <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                  <FaBriefcase className="inline mr-2" style={{ color: '#00A86B' }} />
                  Trade References
                </h2>
                <button
                  onClick={toggleReferencesVerified}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    referencesVerified 
                      ? 'bg-green-100 text-green-700 border border-green-300' 
                      : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {referencesVerified ? (
                    <>
                      <FaCheckCircle className="text-green-500" size={14} />
                      Verified
                    </>
                  ) : (
                    <>
                      <FaCheck size={14} />
                      Verify References
                    </>
                  )}
                </button>
              </div>
              {referencesVerified && (
                <div className="mb-3 text-sm text-green-600 flex items-center gap-2">
                  <FaCheckCircle /> References have been verified
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {references.map((ref, index) => (
                  <div key={index} className={`bg-gray-50 rounded-lg p-4 border ${referencesVerified ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between">
                      <p className="font-medium text-navy">{ref.company_name}</p>
                      {referencesVerified && (
                        <FaCheckCircle className="text-green-500" size={16} />
                      )}
                    </div>
                    <div className="space-y-1 text-sm mt-2">
                      <p><span className="text-gray-500">Contact:</span> {ref.contact_person}</p>
                      <p><span className="text-gray-500">Phone:</span> {ref.phone}</p>
                      {ref.email && <p><span className="text-gray-500">Email:</span> {ref.email}</p>}
                      {ref.relationship && <p><span className="text-gray-500">Relationship:</span> {ref.relationship}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DOCUMENT VERIFICATION - references_verified removed */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaShieldAlt className="inline mr-2" style={{ color: '#00A86B' }} />
              Document Verification
            </h2>
            <div className="space-y-3">
              <VerificationItem
                field="cipc_document_verified"
                label="CIPC Registration Document"
                icon={<FaFileAlt className="text-gray-400" />}
                docUrl={client.cipc_document_url}
              />
              <VerificationItem
                field="proof_of_address_verified"
                label="Proof of Address"
                icon={<FaMapMarkerAlt className="text-gray-400" />}
                docUrl={client.proof_of_address_url}
              />
              <VerificationItem
                field="director_id_verified"
                label="Director ID / Passport"
                icon={<FaIdCard className="text-gray-400" />}
                docUrl={client.director_id_url}
              />
              <VerificationItem
                field="proof_of_account_verified"
                label="Proof of Account (Bank Confirmation Letter)"
                icon={<FaFileAlt className="text-gray-400" />}
                docUrl={client.proof_of_account_url}
              />
              {isCredit && (
                <VerificationItem
                  field="bank_statements_verified"
                  label="6 Months Bank Statements"
                  icon={<FaFileAlt className="text-gray-400" />}
                  docUrl={client.bank_statements_url}
                />
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Summary & Actions */}
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-white rounded-xl border p-6 sticky top-24" style={{ borderColor: '#E8ECEF' }}>
            <h3 className="font-semibold text-navy mb-4">Verification Summary</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Verified Items</span>
                <span className="font-medium" style={{ color: allVerified ? '#00A86B' : '#0A1929' }}>
                  {verifiedCount}/{totalItems}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={getStatusBadge(client.status)}>
                  {getStatusLabel(client.status)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Client Type</span>
                <span className={getClientTypeBadge(client.client_type)}>
                  {client.client_type || 'prepaid'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Submitted</span>
                <span className="text-xs">{formatDate(client.created_at)}</span>
              </div>
              {client.approved_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Approved</span>
                  <span className="text-xs">{formatDate(client.approved_at)}</span>
                </div>
              )}
              {isCredit && (
                <div className="flex justify-between">
                  <span className="text-gray-500">References</span>
                  <span className={`font-medium ${referencesVerified ? 'text-green-600' : 'text-yellow-600'}`}>
                    {referencesVerified ? '✅ Verified' : '⏳ Pending'}
                  </span>
                </div>
              )}
            </div>

            <hr className="my-4" style={{ borderColor: '#E8ECEF' }} />

            {/* Credit Details (if credit client) */}
            {isCredit && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <h4 className="text-sm font-medium text-purple-700 mb-2">Credit Application</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Requested Limit</span>
                    <span className="font-medium text-purple-700">{formatCurrency(client.requested_credit_limit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Requested Terms</span>
                    <span className="font-medium text-purple-700">{client.requested_payment_terms || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">References</span>
                    <span className="font-medium text-purple-700">{references?.length || 0}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Admin Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Notes
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green text-sm"
                style={{ borderColor: '#E8ECEF' }}
                rows="3"
                placeholder="Add overall notes about this application..."
              />
            </div>

            <hr className="my-4" style={{ borderColor: '#E8ECEF' }} />

            {/* Action Buttons */}
            <div className="space-y-3">
              {client.status === 'pending' && (
                <>
                  {/* Credit Approval Fields (for credit clients) */}
                  {isCredit && (
                    <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-600">Credit Approval Settings</p>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Approved Credit Limit (R) *
                        </label>
                        <input
                          type="number"
                          value={approveData.approved_credit_limit}
                          onChange={(e) => setApproveData({ ...approveData, approved_credit_limit: e.target.value })}
                          placeholder="e.g. 100000"
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
                          style={{ borderColor: '#E8ECEF' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Approved Payment Terms *
                        </label>
                        <select
                          value={approveData.approved_payment_terms}
                          onChange={(e) => setApproveData({ ...approveData, approved_payment_terms: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
                          style={{ borderColor: '#E8ECEF' }}
                        >
                          <option value="COD">COD (Cash on Delivery)</option>
                          <option value="7 Days">7 Days</option>
                          <option value="15 Days">15 Days</option>
                          <option value="30 Days">30 Days</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleApproveClick}
                    disabled={processing || !allVerified}
                    className="w-full px-4 py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: allVerified ? '#00A86B' : '#94A3B8' }}
                    onMouseEnter={(e) => {
                      if (allVerified) e.currentTarget.style.backgroundColor = '#00995E';
                    }}
                    onMouseLeave={(e) => {
                      if (allVerified) e.currentTarget.style.backgroundColor = '#00A86B';
                    }}
                  >
                    {processing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                    {processing ? 'Processing...' : '✅ Approve Application'}
                  </button>
                  {!allVerified && (
                    <p className="text-xs text-yellow-600 text-center">
                      <FaExclamationTriangle className="inline mr-1" size={12} />
                      Verify all {totalItems - verifiedCount} remaining items before approving
                    </p>
                  )}
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={processing}
                    className="w-full px-4 py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#EF4444' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EF4444'}
                  >
                    <FaTimes /> Reject Application
                  </button>
                </>
              )}
              {client.status === 'active' && (
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <FaCheckCircle className="text-3xl text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">Client Approved</p>
                  <p className="text-xs text-green-600">Approved on {formatDate(client.approved_at)}</p>
                  {isCredit && (
                    <div className="mt-2 text-xs text-purple-600">
                      Credit Limit: {formatCurrency(client.approved_credit_limit)} • Terms: {client.approved_payment_terms}
                    </div>
                  )}
                </div>
              )}
              {client.status === 'rejected' && (
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <FaTimes className="text-3xl text-red-500 mx-auto mb-2" />
                  <p className="text-red-700 font-medium">Client Rejected</p>
                  {client.rejection_reason && (
                    <p className="text-xs text-red-600 mt-1">Reason: {client.rejection_reason}</p>
                  )}
                </div>
              )}
            </div>

            <hr className="my-4" style={{ borderColor: '#E8ECEF' }} />
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <span className="text-gray-500">Client Type</span>
                <p className="font-bold text-navy capitalize">{client.client_type || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <span className="text-gray-500">Documents</span>
                <p className="font-bold text-navy">
                  {Object.values({
                    proof_of_account_url: client.proof_of_account_url,
                    cipc_document_url: client.cipc_document_url,
                    director_id_url: client.director_id_url,
                    proof_of_address_url: client.proof_of_address_url
                  }).filter(Boolean).length}/4 Uploaded
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ STYLED APPROVE MODAL */}
      {showApproveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowApproveModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="text-3xl text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-navy">Approve Client</h2>
              <p className="text-sm text-gray-500 mt-2">
                Are you sure you want to approve <strong className="text-navy">{client?.company_name}</strong>'s application?
              </p>
              {isCredit && (
                <div className="mt-3 p-3 bg-purple-50 rounded-lg text-left text-sm">
                  <p className="font-medium text-purple-700">Credit Details:</p>
                  <p className="text-gray-600">Limit: <strong>{formatCurrency(approveData.approved_credit_limit)}</strong></p>
                  <p className="text-gray-600">Terms: <strong>{approveData.approved_payment_terms}</strong></p>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-3">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowApproveModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                style={{ backgroundColor: 'white', color: '#4A5568' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                disabled={processing}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white hover:bg-green transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#00A86B' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
              >
                {processing ? (
                  <>
                    <span className="animate-spin">⏳</span> Processing...
                  </>
                ) : (
                  <>
                    <FaCheckCircle size={14} /> Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ STYLED REJECT MODAL */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRejectModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTimes className="text-3xl text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-600">Reject Application</h2>
              <p className="text-sm text-gray-500 mt-2">
                Please provide a reason for rejecting <strong className="text-navy">{client?.company_name}</strong>'s application.
              </p>
            </div>
            
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500 text-sm"
              rows="4"
              placeholder="Explain why this application is being rejected..."
            />
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                style={{ backgroundColor: 'white', color: '#4A5568' }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EF4444'}
              >
                {processing ? (
                  <>
                    <span className="animate-spin">⏳</span> Processing...
                  </>
                ) : (
                  <>
                    <FaTimes size={14} /> Reject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminClientApprovalDetail;